import React, { useState } from 'react';
import { useAuth } from '../lib/auth';
import { Link, useNavigate } from 'react-router-dom';
import { getFriendlyErrorMessage } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { LogIn } from 'lucide-react';
import BrandLogo from '../components/BrandLogo';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleLogin = async (e) => {
    e.preventDefault();
    if (loading) return;
    setLoading(true);

    try {
      await login(email, password);
      toast.success('Login successful!');
      navigate('/dashboard');
    } catch (error) {
      toast.error(getFriendlyErrorMessage(error, 'Login failed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-industrial-bg text-slate-200" data-testid="login-page">
      {/* Left panel — fully offline, zero CDN dependency */}
      <div className="hidden lg:flex lg:w-1/2 relative bg-[#0A0A0A] border-r border-industrial-border flex-col justify-between p-12">
        {/* Amber left accent bar */}
        <div className="absolute left-0 inset-y-0 w-1 bg-accent" />

        {/* Top: brand */}
        <div>
          <p className="font-mono text-[10px] text-accent uppercase tracking-[0.3em] mb-5">
            SYSTEM ONLINE
          </p>
          <BrandLogo size={56} className="mb-4" />
          <p className="font-mono text-xs text-slate-500 uppercase tracking-widest">
            Production-ready - Offline-first - Multi-tenant
          </p>
        </div>

        {/* Mid: non-technical product lines */}
        <div className="space-y-3 border-t border-industrial-border pt-8">
          {[
            ['WORKFLOW',  'Coil Inward to Dispatch'],
            ['CONTROL',   'Role-wise Access and Actions'],
            ['TRACKING',  'Weights, Recovery, and Settlements'],
            ['RELIABLE',  'Offline Queue and Auto Sync'],
            ['READY',     'Internal Testing Environment'],
          ].map(([key, val]) => (
            <div key={key} className="flex gap-4 font-mono text-[11px]">
              <span className="text-slate-600 uppercase tracking-wider w-24 shrink-0">{key}</span>
              <span className="text-slate-300">{val}</span>
            </div>
          ))}
        </div>

        {/* Bottom: build stamp */}
        <p className="font-mono text-[10px] text-slate-700 uppercase tracking-widest">
          INTERNAL OPERATIONS PLATFORM
        </p>
      </div>
      
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 bg-industrial-bg border-l border-industrial-border">
        <div className="w-full max-w-md">
          <div className="mb-8">
            <h2 className="font-mono text-2xl font-bold tracking-wider text-white uppercase mb-2">Welcome Back</h2>
            <p className="text-xs font-mono text-slate-400 uppercase tracking-wide">Enter your credentials to access the system</p>
          </div>

          <form onSubmit={handleLogin} className="space-y-6" data-testid="login-form">
            <div className="space-y-1 text-left">
              <Label htmlFor="email" className="text-xs font-semibold uppercase tracking-wider text-slate-400">Email</Label>
              <Input
                id="email"
                type="email"
                data-testid="email-input"
                placeholder="operator@metalworks.in"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="h-12 bg-industrial-s2 border border-industrial-border text-white font-mono rounded-none focus:border-accent focus:ring-0"
              />
            </div>

            <div className="space-y-1 text-left">
              <Label htmlFor="password" className="text-xs font-semibold uppercase tracking-wider text-slate-400">Password</Label>
              <Input
                id="password"
                type="password"
                data-testid="password-input"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="h-12 bg-industrial-s2 border border-industrial-border text-white font-mono rounded-none focus:border-accent focus:ring-0"
              />
            </div>

            <button
              type="submit"
              data-testid="login-submit-button"
              className="w-full h-12 bg-accent hover:bg-accent-hover text-white font-mono font-bold uppercase tracking-wider rounded-none border-b-2 border-black/30 flex items-center justify-center gap-2 transition-colors duration-75"
              disabled={loading}
            >
              {loading ? (
                <span>Authenticating...</span>
              ) : (
                <>
                  <LogIn className="h-4 w-4" />
                  Sign In
                </>
              )}
            </button>
          </form>
          <p className="mt-6 font-mono text-xs text-slate-500 text-center uppercase tracking-wider">
            New company? <Link to="/signup" className="text-accent underline hover:text-accent-hover ml-1">Start free trial</Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;
